/**
 * Natural rounding instead of truncation when dividing BigInts.
 */
export function roundDiv(a: bigint, b: bigint) {
	if (b === 0n) throw new RangeError("Division by zero");
	const absA = a < 0n ? -a : a;
	const absB = b < 0n ? -b : b;
	const q = absA / absB; // integer part of |a|/|b|
	const r = absA % absB; // remainder

	const sign = a < 0n === b < 0n ? 1n : -1n;

	if (a < 0n === b < 0n) {
		// Positive result: tie (exactly 0.5) rounds up.
		return sign * (2n * r >= absB ? q + 1n : q);
	} else {
		// Negative result: tie rounds toward 0.
		return sign * (2n * r > absB ? q + 1n : q);
	}
}

export class HighPrecisionNumber {
	// add 4 decimal places
	static readonly PRECISION = 10000n;

	static readonly ZERO = new HighPrecisionNumber(0n);

	static fromBigInt(value: bigint) {
		return new HighPrecisionNumber(value * HighPrecisionNumber.PRECISION);
	}

	readonly value: bigint;

	private constructor(value: bigint | string) {
		this.value = BigInt(value);
	}

	add(other: HighPrecisionNumber): HighPrecisionNumber;
	add(other: bigint): HighPrecisionNumber;
	add(other: bigint | HighPrecisionNumber): HighPrecisionNumber {
		if (other instanceof HighPrecisionNumber) {
			return new HighPrecisionNumber(this.value + other.value);
		}
		return new HighPrecisionNumber(this.value + HighPrecisionNumber.fromBigInt(other).value);
	}

	subtract(other: HighPrecisionNumber): HighPrecisionNumber;
	subtract(other: bigint): HighPrecisionNumber;
	subtract(other: bigint | HighPrecisionNumber): HighPrecisionNumber {
		if (other instanceof HighPrecisionNumber) {
			return new HighPrecisionNumber(this.value - other.value);
		}
		return new HighPrecisionNumber(this.value - HighPrecisionNumber.fromBigInt(other).value);
	}

	multiply(other: HighPrecisionNumber): HighPrecisionNumber;
	multiply(other: bigint): HighPrecisionNumber;
	multiply(other: bigint | HighPrecisionNumber): HighPrecisionNumber {
		if (other instanceof HighPrecisionNumber) {
			return new HighPrecisionNumber((this.value * other.value) / HighPrecisionNumber.PRECISION);
		}
		return new HighPrecisionNumber(
			(this.value * HighPrecisionNumber.fromBigInt(other).value) / HighPrecisionNumber.PRECISION,
		);
	}

	divide(other: HighPrecisionNumber): HighPrecisionNumber;
	divide(other: bigint): HighPrecisionNumber;
	divide(other: bigint | HighPrecisionNumber): HighPrecisionNumber {
		if (other instanceof HighPrecisionNumber) {
			return new HighPrecisionNumber((HighPrecisionNumber.PRECISION * this.value) / other.value);
		}
		return new HighPrecisionNumber(
			(HighPrecisionNumber.PRECISION * this.value) / HighPrecisionNumber.fromBigInt(other).value,
		);
	}

	toBigInt() {
		return roundDiv(this.value, HighPrecisionNumber.PRECISION);
	}
	valueOf() {
		return this.toBigInt();
	}
	round() {
		return HighPrecisionNumber.fromBigInt(roundDiv(this.value, HighPrecisionNumber.PRECISION));
	}
	toString() {
		return `HighPrecisionNumber<${this.value}>`;
	}
	[Symbol.for("nodejs.util.inspect.custom")]() {
		return this.toString();
	}
	toJSON() {
		return this.toString();
	}
}
